import { useCallback, useMemo, useRef, useState } from 'react';
import api from 'src/api/api';
import { HEADERS } from 'src/constants/headers';
import { TWorkingLead } from 'src/modules/assistant/types/workingLead';
import { TLoadWorkingLeadsOptions } from '../types';

const WORKING_LEADS_URL = '/ai/lead-chats';
const CURSOR_PARAM = 'cursor';

interface TWorkingLeadsResponse {
    data?: TWorkingLead[];
    pagination?: { nextCursor?: string; hasMore?: boolean };
}

const getWorkingLeadsUrl = (cursor: string | null) =>
    cursor
        ? `${WORKING_LEADS_URL}?${CURSOR_PARAM}=${encodeURIComponent(cursor)}`
        : WORKING_LEADS_URL;

const appendNew = (current: TWorkingLead[], items: TWorkingLead[]) => [
    ...current,
    ...items.filter((item) => !current.some(({ id }) => id === item.id)),
];

/**
 * Paginated working leads (cursor based). A ref guards against concurrent loads,
 * so fast repeated clicks trigger a single request.
 */
export const useWorkingLeads = () => {
    const [workingLeads, setWorkingLeads] = useState<TWorkingLead[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);
    const cursorRef = useRef<string | null>(null);

    const loadWorkingLeads = useCallback(
        async ({ reset = false }: TLoadWorkingLeadsOptions = {}) => {
            if (loadingRef.current) return;
            loadingRef.current = true;
            setLoading(true);
            try {
                const response = await api.get(
                    getWorkingLeadsUrl(reset ? null : cursorRef.current),
                    HEADERS()
                );
                const body = response.data as TWorkingLeadsResponse;
                // Sold leads are not worked anymore; the backend should exclude them
                const items = (body.data || []).filter((item) => !item.hasSale);
                setWorkingLeads((current) =>
                    reset ? items : appendNew(current, items)
                );
                cursorRef.current = body.pagination?.nextCursor || null;
                setHasMore(Boolean(body.pagination?.hasMore));
            } catch (_) {
                if (reset) setWorkingLeads([]);
                setHasMore(false);
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        },
        []
    );

    return useMemo(
        () => ({
            workingLeads,
            workingLeadsHasMore: hasMore,
            workingLeadsLoading: loading,
            loadWorkingLeads,
        }),
        [hasMore, loadWorkingLeads, loading, workingLeads]
    );
};
